import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@/lib/router-compat";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { usePromoCarousel, type PromoSlide } from "@/hooks/usePromoCarousel";
import { renderUrl } from "@/lib/imageUtils";
import { useInteractionReady } from "@/components/ui/InteractionGate";

/**
 * CMS-managed full-width promo banner carousel (dealer.com style strip that
 * sits directly under the homepage hero). Content lives in page_content
 * (page_slug='home', section_key='promo_carousel') and is edited in
 * Admin → Pages → Promo Carousel.
 */
export default function PromoCarousel({ slug = "home" }: { slug?: string }) {
  const { data } = usePromoCarousel(slug);
  const engaged = useInteractionReady();
  const slides: PromoSlide[] = (data?.slides ?? []).filter(
    (s) => s.visible !== false && (s.image_desktop || s.image_mobile),
  );
  const layout = data?.layout ?? {};
  const autoplay = layout.autoplay !== false;
  const interval = layout.interval_ms ?? 6000;
  const showArrows = layout.show_arrows !== false;
  const showDots = layout.show_dots !== false;
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);

  const go = useCallback(
    (next: number) => {
      if (slides.length === 0) return;
      setIdx(((next % slides.length) + slides.length) % slides.length);
    },
    [slides.length],
  );

  useEffect(() => {
    if (!engaged || window.matchMedia("(prefers-reduced-motion: reduce)").matches || !autoplay || slides.length <= 1) return;
    const id = setInterval(() => {
      if (!paused.current) setIdx((c) => (c + 1) % slides.length);
    }, Math.max(2000, interval));
    return () => clearInterval(id);
  }, [engaged, autoplay, interval, slides.length]);

  if (slides.length === 0) return null;

  return (
    <section
      aria-label="Current promotions"
      className="container py-4"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
    >
      <div className="relative overflow-hidden rounded-xl bg-secondary shadow-sm">
        {slides.map((slide, i) => {
          const desktop = slide.image_desktop || slide.image_mobile || "";
          const mobile = slide.image_mobile || slide.image_desktop || "";
          const desktopOptimized = renderUrl(desktop, 1200, 60);
          const mobileOptimized = renderUrl(mobile, 480, 50);
          const active = i === idx;
          const inner = (
            <picture className="block h-full w-full">
              <source media="(min-width: 768px)" srcSet={desktopOptimized} />
              <img
                src={mobileOptimized}
                alt={slide.alt || ""}
                width={1200}
                height={400}
                className="block h-auto w-full object-contain object-center md:h-full"
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
              />

            </picture>
          );
          return (
            <div
              key={`${desktop}-${i}`}
              aria-hidden={!active}
              className={`${active ? "" : "absolute inset-0 "}transition-opacity duration-500 ${
                active ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
            >
              <div className="h-full w-full">
                {slide.href ? (
                  slide.href.startsWith("http") || slide.href.startsWith("#") ? (
                    <a
                      href={slide.href}
                      className="block h-full w-full"
                      tabIndex={active ? 0 : -1}
                      {...(slide.open_in_new_tab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    >
                      {inner}
                    </a>
                  ) : (
                    <Link to={slide.href} className="block h-full w-full" tabIndex={active ? 0 : -1}>
                      {inner}
                    </Link>
                  )
                ) : (
                  inner
                )}
              </div>
            </div>
          );
        })}

        {showArrows && slides.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous promotion"
              onClick={() => go(idx - 1)}
              className="absolute left-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-background/70 text-foreground shadow-sm transition hover:bg-background"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next promotion"
              onClick={() => go(idx + 1)}
              className="absolute right-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-background/70 text-foreground shadow-sm transition hover:bg-background"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {showDots && slides.length > 1 && (
          <div className="absolute bottom-0 left-1/2 z-10 flex -translate-x-1/2 gap-1">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to promotion ${i + 1}`}
                aria-current={i === idx}
                onClick={() => go(i)}
                className="grid h-11 w-11 place-items-center"
              >
                <span
                  aria-hidden
                  className={`block h-2 rounded-full transition-all ${
                    i === idx ? "w-6 bg-primary" : "w-2 bg-background/70"
                  }`}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}